import { useEffect } from "react";
import {
  Drawer,
  Button,
  Input,
  Select,
  Space,
  Form,
} from "antd";
import { SupplierFilterValues } from "..";
import { EnableDisableOptions, YesNoOptions } from "../../../constants/common";
import { IMasterDataSupplier } from "../../../interfaces/master-data";
import MultiSelectTag from "../../../components/multi-select-tag";

const { Option } = Select;

interface FilterSupplierDrawerProps {
  open: boolean;
  onClose: () => void;
  onClear?: () => void;
  onApply: (filters: SupplierFilterValues) => void;
  defaultValues?: SupplierFilterValues;
  masterData: IMasterDataSupplier;
}

const FilterSupplierDrawer: React.FC<FilterSupplierDrawerProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  masterData,
  defaultValues = {},
}) => {
  const [form] = Form.useForm();
  const {
    countries = [],
    currencies = [],
    nominalAccountCredits = [],
    nominalAccountDebits = [],
    users = [],
  } = masterData;

  useEffect(() => {
    if (open && defaultValues) {
      form.setFieldsValue(defaultValues);
    }
  }, [open, defaultValues, form]);

  const handleApply = () => {
    const values = form.getFieldsValue();
    onApply(values);
    onClose?.();
  };

  const handleClear = () => {
    form.resetFields();
    onClear?.();
  };

  return (
    <Drawer
      title="Filter Suppliers"
      onClose={onClose}
      open={open}
      width={520}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={handleClear} type="link">
            Clear All
          </Button>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleApply}>
              Apply
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item label="State" name="isActive">
          <Select allowClear placeholder="All">
            {EnableDisableOptions.map((opt) => (
              <Option key={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Credit Supplier" name="isCreditSupplier">
          <Select allowClear placeholder="All">
            {YesNoOptions.map((opt) => (
              <Option key={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="AP Owner" name="apOwners">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={users.map((item) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))}
          />
        </Form.Item>

        <Form.Item label="Product Owner" name="productOwners">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={users.map((item) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
            }))}
          />
        </Form.Item>

        <Form.Item label="Part of a Group" name="isPartOfGroup">
          <Select allowClear placeholder="All">
            {YesNoOptions.map((opt) => (
              <Option key={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Group Name" name="groupName">
          <Input placeholder="Enter group name" />
        </Form.Item>

        <Form.Item label="Country" name="countrys">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={countries.map((item) => ({
              value: item.code,
              label: item.label,
            }))}
          />
        </Form.Item>

        <Form.Item label="Currency" name="currencys">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={currencies.map((item) => ({
              value: item.code,
              label: `${item.code} - ${item.label}`,
            }))}
            tagDisplay="value"
          />
        </Form.Item>

        <Form.Item label="Nominal Credit Account" name="nominalAccountCredits">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={nominalAccountCredits.map((item) => ({
              value: item.code,
              label: item.label,
            }))}
          />
        </Form.Item>

        <Form.Item label="Nominal Debit Account" name="nominalAccountDebits">
          <MultiSelectTag
            allowClear
            showSearch
            placeholder="All"
            options={nominalAccountDebits.map((item) => ({
              value: item.code,
              label: item.label,
            }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default FilterSupplierDrawer;
